<script setup lang="ts">
import { nextTick, onMounted, ref, watch, PropType } from "vue";
import { useI18n } from "vue-i18n";
import { Trainer, TranslatedProduct } from "../../types/PersologOnlineTypes.ts";
import DataViewSkeleton from "../common/DataViewSkeleton.vue";
import BuyProfilesTab from "./BuyProfilesTab.vue";
import PurchasedProfilesTab from "./PurchasedProfilesTab.vue";
import { isMobileView, scrollToID } from "../../utils/GeneralUtils.ts";
import { ensureBenchmarkReportFavorited, fetchData, filterNotPurchased, groupAndSort } from "../../utils/ProductViewUtils.ts";

const { t, locale } = useI18n();

const purchasedProductsByModel = ref<Record<string, TranslatedProduct[]>>({});
const availableProductsByModel = ref<Record<string, TranslatedProduct[]>>({});
const loading = ref<boolean>(false);
const activeTab = ref("purchased");

defineProps({
    trainer: {
        type: Object as PropType<Trainer>,
        required: true,
    },
});

const emit = defineEmits<{ (e: "update:activeTab", value: string): void }>();

watch(activeTab, (val) => emit("update:activeTab", val), { immediate: true });

async function loadData() {
    loading.value = true;
    let { purchased, all } = await fetchData();
    if (await ensureBenchmarkReportFavorited(purchased)) {
        // in122 was just favorited — refetch so it shows in the stock list and the import option
        ({ purchased, all } = await fetchData());
    }
    const notPurchased = filterNotPurchased(all, purchased);
    purchasedProductsByModel.value = groupAndSort(purchased);
    availableProductsByModel.value = groupAndSort(notPurchased);
    loading.value = false;
}

async function handleUpdate(needsUpdate: boolean, product: string) {
    if (needsUpdate) {
        await loadData();
        activeTab.value = "purchased";
        nextTick(() => scrollToID(`data-view-purchased-products_data-view_${product}_card`, 120));
    }
}

onMounted(loadData);

watch(locale, loadData);
</script>

<template>
    <div v-if="loading" class="mb-8">
        <DataViewSkeleton layout="list" />
    </div>

    <div v-else class="profile-tabs-wrapper">
        <Tabs v-model:value="activeTab">
            <TabList>
                <Tab value="purchased" data-cy="purchased-tab">
                    <i v-if="!isMobileView()" class="pi pi-bookmark mr-2" />
                    {{ t("profile-overview.tabs.purchased") }}
                </Tab>
                <Tab value="buy" data-cy="buy-tab">
                    <i v-if="!isMobileView()" class="pi pi-shopping-cart mr-2" />
                    {{ t("profile-overview.tabs.buy") }}
                </Tab>
            </TabList>

            <TabPanels>
                <TabPanel value="purchased">
                    <PurchasedProfilesTab
                        :trainer="trainer"
                        :purchased-products-by-model="purchasedProductsByModel"
                        @update="handleUpdate"
                    />
                </TabPanel>

                <TabPanel value="buy">
                    <BuyProfilesTab
                        :trainer="trainer"
                        :available-products-by-model="availableProductsByModel"
                        @update="handleUpdate"
                    />
                </TabPanel>
            </TabPanels>
        </Tabs>
    </div>
</template>

<style scoped>
.profile-tabs-wrapper :deep(.p-tablist) {
    background: transparent;
    padding: 0;
    border-bottom: 2px solid var(--p-secondary-500);
}

.profile-tabs-wrapper :deep(.p-tab) {
    font-size: 1rem;
    font-weight: 600;
    padding: 0.875rem 1.75rem;
    color: var(--p-text-muted-color);
    border-radius: 10px 10px 0 0;
    border: 2px solid var(--p-surface-200);
    border-bottom: none;
    margin-bottom: -2px;
    margin-top: 5px;
    margin-left: 5px;
    transition: color 0.15s ease, border-color 0.15s ease, background 0.15s ease;
}

.profile-tabs-wrapper :deep(.p-tab:hover:not(.p-tab-active)) {
    color: var(--p-secondary-500);
    border-color: var(--p-secondary-500);
    background: var(--p-surface-50);
}

.profile-tabs-wrapper :deep(.p-tab.p-tab-active) {
    color: white;
    background: var(--p-secondary-500);
    border-bottom-color: var(--p-secondary-500);
}

.profile-tabs-wrapper :deep(.p-tabpanels) {
    padding: 0;
    background: transparent;
}

.profile-tabs-wrapper :deep(.p-tablist-active-bar) {
    background: var(--p-secondary-500);
}

@media screen and (max-width: 600px) {
    .profile-tabs-wrapper :deep(.p-tablist) {
        display: flex;
    }

    .profile-tabs-wrapper :deep(.p-tab) {
        flex: 1;
        min-width: 0;
        font-size: 0.78rem;
        padding: 0.6rem 0.5rem;
        text-align: center;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
}
</style>

